import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  DEFAULT_FLOW_STATE,
  FLOW_STORAGE_KEY,
  FlowState,
} from '../flow/types';

interface FlowContextValue {
  state: FlowState;
  updateState: (patch: Partial<FlowState>) => void;
  resetFlow: () => void;
}

const FlowContext = createContext<FlowContextValue | undefined>(undefined);

function readStoredState(): FlowState {
  try {
    const raw = sessionStorage.getItem(FLOW_STORAGE_KEY);
    if (!raw) {
      return DEFAULT_FLOW_STATE;
    }
    return { ...DEFAULT_FLOW_STATE, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_FLOW_STATE;
  }
}

function writeStoredState(state: FlowState): void {
  sessionStorage.setItem(FLOW_STORAGE_KEY, JSON.stringify(state));
}

export const FlowProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<FlowState>(readStoredState);

  const updateState = useCallback((patch: Partial<FlowState>) => {
    setState((current) => {
      const next = { ...current, ...patch };
      writeStoredState(next);
      return next;
    });
  }, []);

  const resetFlow = useCallback(() => {
    setState(DEFAULT_FLOW_STATE);
    sessionStorage.removeItem(FLOW_STORAGE_KEY);
  }, []);

  const value = useMemo(
    () => ({ state, updateState, resetFlow }),
    [state, updateState, resetFlow],
  );

  return (
    <FlowContext.Provider value={value}>{children}</FlowContext.Provider>
  );
};

export function useFlow(): FlowContextValue {
  const context = useContext(FlowContext);
  if (!context) {
    throw new Error('useFlow must be used within a FlowProvider');
  }
  return context;
}
